/**
 * The #71 infinite query — the `/watch` screen's server state.
 *
 * `useFeedQuery`'s shape against `/watch/feed`: keyed on the **full** param set (scope, filters,
 * range, sort, search) so any change starts a new query rather than replaying a cursor cut under a
 * different ordering. It sits on its own key root (`watch-feed`, not `watch`), so the constant
 * invalidation the settings tab and the row toggles cause never throws away loaded pages.
 *
 * SSE `watch.detection` invalidates that root: unlike the global feed there is no "must not
 * reorder" rule here — this screen is a catch log, and a fresh detection belongs at the top
 * immediately (§12.2).
 */

import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { MAX_FEED_LIMIT, type WatchFeedQuery, type WatchFeedResponse } from '@lw/shared';
import { getWatchFeed } from '../../api/watch';
import { QK } from '../../api/queryKeys';

export function useWatchFeed(
  params: Partial<WatchFeedQuery>,
  enabled: boolean,
): UseInfiniteQueryResult<InfiniteData<WatchFeedResponse>, Error> {
  const base: Partial<WatchFeedQuery> = { ...params, limit: MAX_FEED_LIMIT };
  return useInfiniteQuery({
    queryKey: QK.watchFeedPage(base),
    queryFn: ({ pageParam, signal }) =>
      getWatchFeed({ ...base, cursor: pageParam ?? undefined }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
  });
}
